<script setup>
import { ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import LocationPickerModal from '@/Components/UI/LocationPickerModal.vue'

const props = defineProps({
  delivery: { type: Object, required: true },
})
const emit = defineEmits(['close'])

const form = useForm({
  address:       props.delivery.address || '',
  lat:           props.delivery.lat ?? null,
  lng:           props.delivery.lng ?? null,
  scheduled_for: props.delivery.scheduled_for
    ? new Date(props.delivery.scheduled_for).toISOString().slice(0, 16)
    : '',
  notes: props.delivery.notes || '',
})

const showPicker = ref(false)

function setLocation({ address, lat, lng }) {
  form.address = address
  form.lat     = lat
  form.lng     = lng
  showPicker.value = false
}

function submit() {
  form.transform(d => ({ delivery: d }))
      .patch(`/deliveries/${props.delivery.id}`, {
        preserveScroll: true,
        onSuccess: () => emit('close'),
      })
}
</script>

<template>
  <div class="overlay" @click.self="emit('close')">
    <div class="modal">
      <header class="modal__hdr">
        <h2 class="modal__title">Edit delivery</h2>
        <button class="modal__x" @click="emit('close')">×</button>
      </header>

      <form id="edit-delivery-form" class="form" @submit.prevent="submit">
        <label class="field">
          <span class="field__label">Address</span>
          <div class="addr-row">
            <input v-model="form.address" required class="field__input addr-input" readonly />
            <button type="button" class="btn" @click="showPicker = true">Pick on map</button>
          </div>
          <span v-if="form.errors.address" class="field__error">{{ form.errors.address }}</span>
        </label>

        <label class="field">
          <span class="field__label">Scheduled time</span>
          <input v-model="form.scheduled_for" type="datetime-local" class="field__input" />
        </label>

        <label class="field">
          <span class="field__label">Notes</span>
          <textarea v-model="form.notes" rows="2" class="field__input field__textarea"></textarea>
        </label>

        <div class="form__actions">
          <button type="button" class="btn" @click="emit('close')">Cancel</button>
          <button type="submit" class="btn btn--primary" :disabled="form.processing">
            {{ form.processing ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <LocationPickerModal
    :open="showPicker"
    title="Set delivery address"
    :address="form.address"
    :lat="form.lat"
    :lng="form.lng"
    @save="setLocation"
    @close="showPicker = false"
  />
</template>

<style scoped>
.overlay {
  position: fixed; inset: 0; z-index: 60;
  background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.modal {
  background: var(--surface);
  border-radius: 0.75rem;
  padding: 1.25rem;
  width: 100%; max-width: 30rem;
  box-shadow: var(--shadow-lg, 0 20px 60px rgba(0,0,0,.25));
}
.modal__hdr {
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 1rem;
}
.modal__title {
  font-size: 1rem; font-weight: 600; color: var(--ink); margin: 0;
}
.modal__x {
  background: none; border: 0; font-size: 1.5rem;
  color: var(--muted); cursor: pointer; line-height: 1;
}
.form { display: grid; gap: 0.875rem; }
.field { display: grid; gap: 0.3rem; }
.field__label { font-size: 0.8125rem; color: var(--inkSoft); }
.field__input {
  padding: 0.5rem 0.75rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-input, 8px);
  color: var(--ink);
  font: inherit;
  font-size: 0.9375rem;
}
.field__textarea { resize: vertical; }
.field__error { font-size: 0.75rem; color: var(--danger); }
.addr-row { display: flex; gap: 0.5rem; }
.addr-input { flex: 1; min-width: 0; }
.form__actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.25rem; }
.btn {
  padding: 0.45rem 0.85rem;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  border-radius: var(--radius-button, 8px);
  cursor: pointer; font-weight: 500; font-size: 0.875rem;
}
.btn--primary {
  background: var(--accent); color: var(--accentInk); border-color: var(--accent);
}
.btn--primary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
